import api from './api';
import { unwrapList, unwrapObject, type ListResult } from './api-envelope';
import type { ServiceListQuery } from './queryKeys';
import type {
  ServiceTenantType,
  CreateServiceInput,
  UpdateServiceInput,
} from '@tcr-erp/shared';
import { type ServiceState } from '@tcr-erp/shared';

export interface ServiceExportData {
  services: Array<Record<string, unknown>>;
}

// Strip server-managed fields that the backend rejects/ignores on create & update
// (Mongoose document metadata leaked back from GET responses when editing/cloning).
function sanitizeBody<T extends CreateServiceInput | UpdateServiceInput>(body: T): T {
  const {
    _id: _id,
    created_at: _ca,
    updated_at: _ua,
    tenant_id: _tid,
    sold_count: _sc,
    ...clean
  } = body as T & {
    _id?: unknown;
    created_at?: unknown;
    updated_at?: unknown;
    tenant_id?: unknown;
    sold_count?: unknown;
  };
  return clean as T;
}

export const serviceService = {
  list: (params: ServiceListQuery): Promise<ListResult<ServiceTenantType>> =>
    api.get('/services', { params }).then(unwrapList<ServiceTenantType>),

  getById: (id: string): Promise<ServiceTenantType> =>
    api.get(`/services/${id}`).then(unwrapObject<ServiceTenantType>),

  create: (body: CreateServiceInput): Promise<ServiceTenantType> =>
    api.post('/services', sanitizeBody(body)).then(unwrapObject<ServiceTenantType>),

  update: (id: string, body: UpdateServiceInput): Promise<ServiceTenantType> =>
    api.put(`/services/${id}`, sanitizeBody(body)).then(unwrapObject<ServiceTenantType>),

  updateState: (id: string, state: ServiceState): Promise<ServiceTenantType> =>
    api.patch(`/services/${id}/state`, { state }).then(unwrapObject<ServiceTenantType>),

  remove: (id: string): Promise<void> =>
    api.delete(`/services/${id}`).then(() => undefined),

  clone: (id: string): Promise<{ _id: string; name: string }> =>
    api.post(`/services/${id}/clone`).then(unwrapObject<{ _id: string; name: string }>),

  bulkUpdateState: (ids: string[], state: ServiceState): Promise<{ updated: number }> =>
    api.patch('/services/bulk-state', { ids, state }).then(unwrapObject<{ updated: number }>),

  exportList: (params: Omit<ServiceListQuery, 'page' | 'page_size'>): Promise<ServiceExportData> =>
    api.get('/services/export', { params }).then(unwrapObject<ServiceExportData>),
};
